import { call, put, select, fork, join, delay } from 'redux-saga/effects';
import { parseDiscoveryBody } from '@vulog/auth';
import { messagesShow } from '../../redux/messages/actions';
import { setAuthMethods, loginSuccessful, loginFailed } from '../../redux/login/actions';
import UserClient from '../../api/UserClient';
import { loginMicrosoft, setDiscoveredAuthorizeEndpoint } from '../../auth/authClient';
import { postRedirect } from './utils';
import { userGetRequest } from '../../redux/user/actions';
import { getAzureMethods, getOAuth2Method } from '../../helpers/auth';
import { logError } from '../../helpers/bugLogger';

let isInitializingAzureAuth = false;
let initializationTask = null;

/**
 * Fetch the tenant's auth methods, normalize them, and publish them to Redux
 * and to the auth adapter. Exported for unit tests; callers should go through
 * `initAzureAuthSaga`, which dedupes concurrent runs.
 *
 * @returns {Generator} redux-saga effects, returning the normalized methods.
 */
export function* initializeAzureAuthTask() {
    try {
        // Get Azure auth methods and URLs
        const { apiConf } = UserClient;
        const response = yield call(UserClient.discoverAuthMethods, {
            authEndpoint: apiConf.authEndpoint,
            clientId: apiConf.authenticatedAccess.clientId,
        });

        // Normalize with the package parser (maps VULOG/AZURE_AD/OAUTH2 → modes
        // and returns a flat list). parseDiscoveryBody expects a { auth_methods }
        // root; some tenants return the methods map directly, so wrap that case.
        const authMethods = parseDiscoveryBody(
            response && response.auth_methods ? response : { auth_methods: response }
        );

        if (!authMethods.length) {
            console.error('No auth methods discovered:', response);
            throw new Error('No auth methods discovered');
        }

        // The Microsoft logout URL is now stored by the Service Worker at
        // login time (loginMicrosoft's logoutUrl); nothing to set here.
        yield put(setAuthMethods(authMethods));

        // The interactive OAuth2 redirect targets the tenant's ABSOLUTE
        // authorize URL, which only exists once discovery has run. Hand it to
        // the auth adapter, which resolves it at navigation time.
        const oauth2Method = getOAuth2Method(authMethods);
        yield call(
            setDiscoveredAuthorizeEndpoint,
            oauth2Method && oauth2Method.urls ? oauth2Method.urls.url_vauth_oauth2_authorize : undefined
        );
        return authMethods;
    } catch (error) {
        console.error('Error initializing Azure authentication:', error);
        throw error;
    }
}

export function* initAzureAuthSaga() {
    const existingAuthMethods = yield select(state => state.login.authMethods);
    if (existingAuthMethods && existingAuthMethods.length > 0) {
        return;
    }

    if (isInitializingAzureAuth && initializationTask) {
        try {
            yield join(initializationTask);
            const updatedAuthMethods = yield select(state => state.login.authMethods);
            if (updatedAuthMethods && updatedAuthMethods.length > 0) {
                return;
            }
        } catch (error) {
            console.error('Previous Azure auth initialization failed:', error);
            isInitializingAzureAuth = false;
            initializationTask = null;
        }
    }

    if (!isInitializingAzureAuth) {
        isInitializingAzureAuth = true;

        initializationTask = yield fork(initializeAzureAuthTask);

        try {
            yield join(initializationTask);
        } catch (error) {
            // Reported here rather than downstream: this is the only place that
            // knows discovery itself failed. Without it a tenant whose
            // `.well-known/method` is down loses the SSO button and the bridge,
            // and the operator hears about it from users.
            console.error('Error initializing Azure authentication:', error);
            logError(error instanceof Error ? error : new Error(String(error)));
        } finally {
            isInitializingAzureAuth = false;
            initializationTask = null;
        }
    }
}

export function* loginWithAzure() {
    try {
        const authMethods = yield select(state => state.login.authMethods);
        const azureMethods = getAzureMethods(authMethods);
        const isAzureAuthEnabled = azureMethods.length > 0;

        if (isAzureAuthEnabled) {
            const azure = azureMethods[0];
            const { apiConf } = UserClient;

            postRedirect(azure.urls.url_authorize, {
                client_id: apiConf.authenticatedAccess.clientId,
                client_secret: apiConf.authenticatedAccess.clientSecret,
            });
        } else {
            yield put(messagesShow('Azure authentication not configured', 'error'));
        }
    } catch (error) {
        console.error('Error during Azure login:', error);
        yield put(messagesShow('azureLoginFailure', 'error'));
    }
}

export function* getValidAzureMethod() {
    let authMethods = yield select(state => state.login.authMethods);
    let azureMethods = getAzureMethods(authMethods);

    // Only initialize if authMethods don't exist or are empty (not if they're an empty list from failed init)
    const shouldInitialize = !authMethods || (authMethods.length === 0 && azureMethods.length === 0);

    if (shouldInitialize) {
        try {
            yield call(initAzureAuthSaga);
            // Small delay to ensure Redux state is updated
            yield delay(50);
            authMethods = yield select(state => state.login.authMethods);
            azureMethods = getAzureMethods(authMethods);
        } catch (error) {
            console.error('Error getting Azure method:', error);
            authMethods = yield select(state => state.login.authMethods);
            azureMethods = getAzureMethods(authMethods);
        }
    }

    if (!azureMethods.length) {
        throw new Error('Azure authentication not configured');
    }

    return azureMethods[0];
}

/**
 * Handle Azure redirection after successful authentication
 * This saga is triggered when the app detects it has been redirected from Azure
 */
export function* handleAzureRedirect() {
    try {
        yield call(initAzureAuthSaga);

        const authMethods = yield select(state => state.login.authMethods);
        if (!authMethods || authMethods.length === 0) {
            throw new Error('Azure authentication methods not available after initialization');
        }

        const azureMethod = yield call(getValidAzureMethod);

        // Exchange the Microsoft session for tokens inside the Service Worker
        // (it GETs the URL with credentials and persists the record, incl. the
        // refresh + logout URLs). Uses url_refresh as both login and refresh
        // URL, mirroring the previous behavior.
        const result = yield call(loginMicrosoft, {
            loginUrl: azureMethod.urls.url_refresh,
            refreshUrl: azureMethod.urls.url_refresh,
            logoutUrl: azureMethod.urls.url_logout,
        });

        if (!result.ok) {
            throw new Error(result.message || 'Failed to get token from Azure');
        }

        // Dispatch successful login action
        yield put(loginSuccessful());

        // Fetch user data (crucial step)
        yield put(userGetRequest());

        // Clear URL parameters to prevent redirect loop
        window.history.replaceState({}, document.title, window.location.pathname);

        // Navigate to the main app page
        window.location.href = '/account';
    } catch (error) {
        console.error('Error handling Azure redirect:', error);
        yield put(loginFailed());
        yield put(messagesShow('azureAuthFailure', 'error'));

        // Clear URL parameters even on error
        window.history.replaceState({}, document.title, window.location.pathname);
    }
}
